import { ReactiveControllerHost, CSSResultGroup, CSSResult } from 'lit';

/**
 * LightDomStyles ReactiveController.
 *
 * This controller injects styles into the light DOM, including support for media queries.
 *
 * @param host The host element.
 */
export class LightDomStylesFull {
  host: ReactiveControllerHost & Element;
  cssStyles: CSSResultGroup;

  constructor(host: ReactiveControllerHost & Element, cssStyles: CSSResultGroup) {
    this.host = host;
    this.cssStyles = cssStyles;
    host.addController(this);
  }

  hostConnected(): void {
    this._injectStyles();
  }

  /**
   * Process and inject styles into the light DOM or document head.
   */
  private _injectStyles(): void {
    const scopeId = this.host.tagName.toLowerCase();
    const styleContent = this._generateScopedCSS(this.cssStyles, scopeId);

    // Inject styles into a <style> element in the <head>.
    const styleElement = document.createElement('style');
    styleElement.textContent = styleContent;
    document.head.prepend(styleElement); // You can prepend or append.
  }

  /**
   * Generate scoped CSS from a CSSResultGroup.
   */
  private _generateScopedCSS(cssStyles: CSSResultGroup, scopeId: string): string {
    let cssText = '';

    // Handle arrays of styles or a single CSSResult
    if (Array.isArray(cssStyles)) {
      for (const style of cssStyles) {
        if (style instanceof CSSResult) {
          cssText += this._scopeCSS(style.cssText, scopeId);
        }
      }
    } else if (cssStyles instanceof CSSResult) {
      cssText += this._scopeCSS(cssStyles.cssText, scopeId);
    }

    return cssText;
  }

  /**
   * Scope CSS by prefixing all selectors with the scopeId.
   * Handles media queries and regular rules.
   */
  private _scopeCSS(cssText: string, scopeId: string): string {
    const ruleRegex = /([^{]+)\{([\s\S]+?)\}/g;

    return cssText.replace(ruleRegex, (_: string, selectors: string, properties: any) => {
      // Handle media queries
      if (selectors.trim().startsWith('@media')) {
        return `${selectors} { ${this._scopeCSS(properties, scopeId)} }`;
      }

      // Prefix selectors with scopeId
      const scopedSelectors = selectors
        .split(',')
        .map((selector: string) => {
          selector = selector.trim();
          if (selector.includes(':host')) {
            return selector.replace(':host', scopeId);
          } else if (selector.includes('::slotted(')) {
            return selector.replace('::slotted(', `${scopeId} `).replace(')', '');
          }
          return `${scopeId} ${selector}`;
        })
        .join(', ');

      return `${scopedSelectors} { ${properties} }`;
    });
  }
}
